import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import type { CustomerFilters } from '@repo/types';
import { CareerLevel, JobSearchStatus, WorkAuthorization, AvailabilityStatus } from '@repo/types';
import { Search, X, SlidersHorizontal, User, MapPin, Briefcase, TrendingUp } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { FilterChips, type FilterChip } from '@/components/ui/filter-chips';

interface CustomerFilterBarProps {
  filters: CustomerFilters;
  onFiltersChange: (filters: CustomerFilters) => void;
}

const subscriptionTiers = [90, 250, 500, 1000];
const subscriptionStatuses = ['active', 'inactive', 'churned'];
const careerLevels = Object.values(CareerLevel);
const jobSearchStatuses = Object.values(JobSearchStatus);
const workAuthStatuses = Object.values(WorkAuthorization);
const availabilityStatuses = Object.values(AvailabilityStatus);

export function CustomerFilterBar({ filters, onFiltersChange }: CustomerFilterBarProps) {
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);

  // Advanced filter states
  const [selectedCareerLevels, setSelectedCareerLevels] = useState<string[]>(filters.careerLevel || []);
  const [selectedJobSearchStatuses, setSelectedJobSearchStatuses] = useState<string[]>(filters.jobSearchStatus || []);
  const [selectedWorkAuth, setSelectedWorkAuth] = useState<string[]>(filters.workAuthorization || []);
  const [selectedAvailability, setSelectedAvailability] = useState<string[]>(filters.availabilityStatus || []);
  const [locationInput, setLocationInput] = useState(filters.location || '');
  const [minExperience, setMinExperience] = useState<string>(filters.minExperience?.toString() || '');
  const [maxExperience, setMaxExperience] = useState<string>(filters.maxExperience?.toString() || '');

  // Debounce search input
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  // Apply debounced search to filters
  useEffect(() => {
    // Only update if search value actually changed to prevent loops
    if (filters.search !== debouncedSearch) {
      onFiltersChange({
        ...filters,
        search: debouncedSearch || undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleSubscriptionTierChange = (value: string) => {
    onFiltersChange({
      ...filters,
      subscriptionTier: value === 'all' ? undefined : Number(value),
    });
  };

  const handleSubscriptionStatusChange = (value: string) => {
    onFiltersChange({
      ...filters,
      subscriptionStatus: value === 'all' ? undefined : (value as CustomerFilters['subscriptionStatus']),
    });
  };

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  }, []);

  const handleResetAdvancedFilters = useCallback(() => {
    setSelectedCareerLevels([]);
    setSelectedJobSearchStatuses([]);
    setSelectedWorkAuth([]);
    setSelectedAvailability([]);
    setLocationInput('');
    setMinExperience('');
    setMaxExperience('');
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchInput('');
    setSelectedCareerLevels([]);
    setSelectedJobSearchStatuses([]);
    setSelectedWorkAuth([]);
    setSelectedAvailability([]);
    setLocationInput('');
    setMinExperience('');
    setMaxExperience('');
    onFiltersChange({
      subscriptionTier: undefined,
      subscriptionStatus: undefined,
      search: undefined,
      careerLevel: undefined,
      jobSearchStatus: undefined,
      workAuthorization: undefined,
      availabilityStatus: undefined,
      location: undefined,
      minExperience: undefined,
      maxExperience: undefined,
    });
  }, [onFiltersChange]);

  const toggleCareerLevel = useCallback((level: string) => {
    setSelectedCareerLevels((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]
    );
  }, []);

  const toggleJobSearchStatus = useCallback((status: string) => {
    setSelectedJobSearchStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  }, []);

  const toggleWorkAuth = useCallback((auth: string) => {
    setSelectedWorkAuth((prev) =>
      prev.includes(auth) ? prev.filter((a) => a !== auth) : [...prev, auth]
    );
  }, []);

  const toggleAvailability = useCallback((avail: string) => {
    setSelectedAvailability((prev) =>
      prev.includes(avail) ? prev.filter((a) => a !== avail) : [...prev, avail]
    );
  }, []);

  const hasActiveFilters =
    filters.subscriptionTier ||
    filters.subscriptionStatus ||
    filters.search ||
    (filters.careerLevel && filters.careerLevel.length > 0) ||
    (filters.jobSearchStatus && filters.jobSearchStatus.length > 0) ||
    (filters.workAuthorization && filters.workAuthorization.length > 0) ||
    (filters.availabilityStatus && filters.availabilityStatus.length > 0) ||
    filters.location ||
    filters.minExperience ||
    filters.maxExperience;

  const activeAdvancedFiltersCount =
    selectedCareerLevels.length +
    selectedJobSearchStatuses.length +
    selectedWorkAuth.length +
    selectedAvailability.length +
    (locationInput ? 1 : 0) +
    (minExperience || maxExperience ? 1 : 0);

  const chips: FilterChip[] = [];
  if (filters.search)
    chips.push({
      key: 'search',
      label: `Search: ${filters.search}`,
      onRemove: () => {
        setSearchInput('');
        onFiltersChange({ ...filters, search: undefined });
      },
    });
  if (filters.subscriptionTier)
    chips.push({
      key: 'tier',
      label: `Tier ${filters.subscriptionTier}`,
      onRemove: () => onFiltersChange({ ...filters, subscriptionTier: undefined }),
    });
  if (filters.subscriptionStatus)
    chips.push({
      key: 'sub-status',
      label: `Status: ${filters.subscriptionStatus}`,
      onRemove: () => onFiltersChange({ ...filters, subscriptionStatus: undefined }),
    });
  if (filters.location)
    chips.push({
      key: 'location',
      label: `Location: ${filters.location}`,
      onRemove: () => {
        setLocationInput('');
        onFiltersChange({ ...filters, location: undefined });
      },
    });
  (filters.careerLevel || []).forEach((val) =>
    chips.push({
      key: `cl-${val}`,
      label: String(val).replace(/_/g, ' '),
      onRemove: () => {
        const next = (filters.careerLevel || []).filter((x) => x !== val);
        onFiltersChange({ ...filters, careerLevel: next.length ? next : undefined });
      },
    })
  );
  (filters.jobSearchStatus || []).forEach((val) =>
    chips.push({
      key: `js-${val}`,
      label: String(val).replace(/_/g, ' '),
      onRemove: () => {
        const next = (filters.jobSearchStatus || []).filter((x) => x !== val);
        onFiltersChange({ ...filters, jobSearchStatus: next.length ? next : undefined });
      },
    })
  );
  (filters.workAuthorization || []).forEach((val) =>
    chips.push({
      key: `wa-${val}`,
      label: String(val).replace(/_/g, ' '),
      onRemove: () => {
        const next = (filters.workAuthorization || []).filter((x) => x !== val);
        onFiltersChange({ ...filters, workAuthorization: next.length ? next : undefined });
      },
    })
  );
  (filters.availabilityStatus || []).forEach((val) =>
    chips.push({
      key: `av-${val}`,
      label: String(val).replace(/_/g, ' '),
      onRemove: () => {
        const next = (filters.availabilityStatus || []).filter((x) => x !== val);
        onFiltersChange({ ...filters, availabilityStatus: next.length ? next : undefined });
      },
    })
  );
  if (filters.minExperience != null || filters.maxExperience != null)
    chips.push({
      key: 'exp',
      label: `Exp: ${filters.minExperience ?? 0}–${filters.maxExperience ?? '∞'}`,
      onRemove: () =>
        onFiltersChange({ ...filters, minExperience: undefined, maxExperience: undefined }),
    });

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      {/* Search */}
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search customers by name, email, or skills..."
          value={searchInput}
          onChange={handleSearchChange}
          className="h-8 pl-9 text-sm"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={filters.subscriptionTier?.toString() || 'all'}
          onValueChange={handleSubscriptionTierChange}
        >
          <SelectTrigger className="h-8 w-[110px] text-xs sm:w-[130px] sm:text-sm">
            <SelectValue placeholder="Tier" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tiers</SelectItem>
            {subscriptionTiers.map((tier) => (
              <SelectItem key={tier} value={tier.toString()}>
                Tier {tier}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.subscriptionStatus || 'all'}
          onValueChange={handleSubscriptionStatusChange}
        >
          <SelectTrigger className="h-8 w-[110px] text-xs sm:w-[130px] sm:text-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {subscriptionStatuses.map((status) => (
              <SelectItem key={status} value={status}>
                {status.replace('_', ' ')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Popover open={isMoreFiltersOpen} onOpenChange={setIsMoreFiltersOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs sm:text-sm">
              <SlidersHorizontal className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">More Filters</span>
              <span className="sm:hidden">More</span>
              {activeAdvancedFiltersCount > 0 && (
                <Badge variant="secondary" className="ml-0.5 h-4 w-4 rounded-full p-0 text-[10px]">
                  {activeAdvancedFiltersCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[320px] p-4 sm:w-[380px]"
            align="end"
            side="bottom"
          >
            <div className="space-y-4">
              <div>
                <h4 className="mb-1 text-sm font-semibold">Advanced Filters</h4>
                <p className="text-xs text-muted-foreground">
                  Refine your customer search
                </p>
              </div>

              {/* Career Level */}
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
                  <Label className="text-xs font-medium">Career Level</Label>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {careerLevels.map((level) => (
                    <Badge
                      key={level}
                      variant={selectedCareerLevels.includes(level) ? 'default' : 'outline'}
                      className="cursor-pointer text-[10px] capitalize"
                      onClick={() => toggleCareerLevel(level)}
                    >
                      {level.replace('_', ' ')}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Job Search Status */}
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <Briefcase className="h-3.5 w-3.5 text-muted-foreground" />
                  <Label className="text-xs font-medium">Job Search Status</Label>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {jobSearchStatuses.map((status) => (
                    <Badge
                      key={status}
                      variant={selectedJobSearchStatuses.includes(status) ? 'default' : 'outline'}
                      className="cursor-pointer text-[10px] capitalize"
                      onClick={() => toggleJobSearchStatus(status)}
                    >
                      {status.replace('_', ' ')}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Work Authorization */}
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                  <Label className="text-xs font-medium">Work Authorization</Label>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {workAuthStatuses.map((auth) => (
                    <Badge
                      key={auth}
                      variant={selectedWorkAuth.includes(auth) ? 'default' : 'outline'}
                      className="cursor-pointer text-[10px] capitalize"
                      onClick={() => toggleWorkAuth(auth)}
                    >
                      {auth.replace('_', ' ')}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Availability */}
              <div className="space-y-2">
                <Label className="text-xs font-medium">Availability</Label>
                <div className="flex flex-wrap gap-1.5">
                  {availabilityStatuses.map((avail) => (
                    <Badge
                      key={avail}
                      variant={selectedAvailability.includes(avail) ? 'default' : 'outline'}
                      className="cursor-pointer text-[10px] capitalize"
                      onClick={() => toggleAvailability(avail)}
                    >
                      {avail.replace('_', ' ')}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Location */}
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                  <Label className="text-xs font-medium">Location</Label>
                </div>
                <Input
                  placeholder="Enter city or region..."
                  value={locationInput}
                  onChange={(e) => setLocationInput(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>

              {/* Experience Range */}
              <div className="space-y-2">
                <Label className="text-xs font-medium">Years of Experience</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] text-muted-foreground">Min</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={minExperience}
                      onChange={(e) => setMinExperience(e.target.value)}
                      min="0"
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] text-muted-foreground">Max</Label>
                    <Input
                      type="number"
                      placeholder="20+"
                      value={maxExperience}
                      onChange={(e) => setMaxExperience(e.target.value)}
                      min="0"
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetAdvancedFilters}
                  className="h-8 flex-1 text-xs"
                >
                  Reset
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            className="h-8 gap-1 px-2 text-xs sm:px-3 sm:text-sm"
          >
            <X className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Clear all</span>
            <span className="sm:hidden">Clear</span>
          </Button>
        )}
      </div>
      </div>
      <FilterChips chips={chips} />
    </div>
  );
}
